import imagesLoaded from 'imagesloaded';
import Masonry from 'masonry-layout';
import Offcanvas from 'smg/offcanvas';
import QueryString from 'smg/querystring';
import { UUID } from 'smg/uuid';

main();

function main() {
    const $galleryImages = document.querySelector<HTMLElement>('#gallery-images');
    if (!$galleryImages) {
        return;
    }

    const masonry = new Masonry($galleryImages, {
        itemSelector: '.gallery-image-parent:not(.d-none)',
    });

    imagesLoaded($galleryImages).on('progress', () => {
        masonry.layout?.();
    });

    const $filterCheckboxes = document.querySelectorAll<HTMLInputElement>('.filter-checkbox');

    const $imgParents = document.querySelectorAll<HTMLAnchorElement>('.gallery-image-parent');
    const imageDetails = new Map<HTMLElement, Map<string, string>>();
    for (const $imageParent of Array.from($imgParents)) {
        const $img = $imageParent.querySelector('img') as HTMLImageElement;
        const detailsJSON = $img.getAttribute('data-details') || '';
        const details = JSON.parse(detailsJSON) as Record<string, string>;

        const detailsMap = new Map<string, string>();
        for (const [key, value] of Object.entries(details)) {
            detailsMap.set(key, value);
        }

        imageDetails.set($imageParent, detailsMap);
    }

    const checkedFilters: () => Record<string, string[]> = () => {
        const filters: Record<string, string[]> = {};
        $filterCheckboxes.forEach($checkbox => {
            if ($checkbox.checked) {
                const filter = String($checkbox.getAttribute('data-filter') || '');
                const value = $checkbox.value;

                if (!filters[filter]) {
                    filters[filter] = [value];
                } else {
                    filters[filter].push(value);
                }
            }
        });

        return filters;
    };

    const setFancyboxGallery = () => {
        const $visibleImages = Array.from(document.querySelectorAll('.gallery-image-parent:not(.d-none)')) as HTMLAnchorElement[];
        const uuid = UUID();
        $visibleImages.forEach($image => {
            $image.setAttribute('data-fancybox', uuid);
        });
    };

    const toggleImages = () => {
        const filters = checkedFilters();

        $imgParents.forEach($ => $.classList.remove('d-none'));

        if (!Object.keys(filters).length) {
            setFancyboxGallery();
            masonry.reloadItems?.();
            masonry.layout?.();

            $filterCheckboxes.forEach($checkbox => {
                $checkbox.disabled = false;
            });
            return;
        }

        const filtersSet = new Map<string, Set<string>>();
        for (const [filter, values] of Object.entries(filters)) {
            filtersSet.set(filter, new Set(values));
        }

        // filterValueSet contains `key=value` pairs for every filter value for the current images.
        // this lets us very easily check if a filter has any images that match it.
        const filterValueSet = new Set<string>();

        // an image must have one value of each filter selected to be shown.
        // filter values are OR'd together, and filters are AND'd together.

        for (const [image, details] of imageDetails) {
            let show = true;

            for (const [filterName, filterValue] of filtersSet) {
                if (!details.has(filterName) || !filterValue.has(details.get(filterName) as string)) {
                    show = false;
                    break;
                }
            }

            if (!show) {
                image.setAttribute('data-fancybox', 'gallery');
                image.classList.add('d-none');
            } else {
                for (const [filterName, filterValue] of details.entries()) {
                    filterValueSet.add(`${filterName}=${filterValue}`);
                }
            }
        }

        setFancyboxGallery();

        masonry.reloadItems?.();
        masonry.layout?.();

        $filterCheckboxes.forEach($checkbox => {
            const filter = $checkbox.getAttribute('data-filter') || '';
            const value = $checkbox.value;

            $checkbox.disabled = !filterValueSet.has(`${filter}=${value}`);
        });
    };

    let filtersQueryString = QueryString().get('filters');
    if (typeof filtersQueryString === 'undefined') {
        if (window.location.host.includes('challengecoins')) {
            filtersQueryString = 'lineItem=Challenge Coins';
        } else if (window.location.host.includes('pins')) {
            filtersQueryString = 'lineItem=Lapel Pins';
        } else if (window.location.host.includes('patches')) {
            filtersQueryString = 'lineItem=Patches';
        } else if (window.location.host.includes('lanyards')) {
            filtersQueryString = 'lineItem=Lanyards';
        }
    }
    if (filtersQueryString) {
        const filters: Record<string, string[]> = filtersQueryString.split('|').reduce((acc: Record<string, string[]>, filter) => {
            const [key, value] = filter.split('=');
            acc[key] = value.split('_');
            return acc;
        }, {});

        Object.entries(filters).forEach(([filter, values]) => {
            values.forEach(value => {
                const $checkbox = document.querySelector<HTMLInputElement>(`input[data-filter="${filter}"][value="${value}"]`);
                if ($checkbox) {
                    $checkbox.checked = true;
                }
            });
        });
    }

    const setQueryString = () => {
        const filters = checkedFilters();
        // values joined by `_`, filters joined by `|`
        QueryString().set(
            'filters',
            Object.entries(filters).
                map(([filter, values]) => `${filter}=${values.join('_')}`).
                join('|'),
        );
    };

    $filterCheckboxes.forEach($checkbox => {
        $checkbox.addEventListener('change', () => {
            setQueryString();
            toggleImages();
        });
    });

    const $filtersParent = document.querySelector('#gallery-filters-parent') as HTMLElement;
    const $filters = document.querySelector('#gallery-filters') as HTMLElement;

    const $showFilters = document.querySelector<HTMLButtonElement>('#gallery-show-filters');
    $showFilters?.addEventListener('click', () => {
        new Offcanvas({
            title: 'Filters',
            body: $filters,
            cancelText: 'Close',
            cancelButtonClass: 'btn btn-secondary m-2 rounded-2',
            onClose() {
                $filtersParent.appendChild($filters);
            },
        });
    });

    toggleImages();
}
